import type { CardPinView, InstrumentRef } from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент закреплений карточек §6.9 запросного канала §7.6 (ТЗ 1.52,
 * §65). Маршрут — `/api/card-pins` одним путём для всех трёх операций:
 * тело `POST` и `DELETE` — сам `InstrumentRef` (change stage8-pin-card, Д1).
 * Тот же стиль различения исходов статусом, что у `favorites-api.ts`.
 */

const CARD_PINS_BASE = "/api/card-pins";

function isInstrumentRef(value: unknown): value is InstrumentRef {
  return (
    isRecord(value) &&
    typeof value.exchange === "string" &&
    (value.market === "spot" || value.market === "perp") &&
    typeof value.base === "string" &&
    typeof value.quote === "string"
  );
}

function isCardPinView(value: unknown): value is CardPinView {
  return (
    isRecord(value) &&
    isInstrumentRef(value.instrument) &&
    (typeof value.exchange_base === "string" || value.exchange_base === null) &&
    typeof value.created_at_ms === "number"
  );
}

/** Список закреплений карточек предъявившего сессию пользователя, целиком. */
export type CardPinsListResult =
  | { status: "ok"; pins: CardPinView[] }
  | { status: "unauthorized" }
  | { status: "unavailable" };

export async function fetchCardPins(): Promise<CardPinsListResult> {
  let response: Response;
  try {
    response = await fetch(CARD_PINS_BASE, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (!response.ok) {
    console.error("список закреплений карточек отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!Array.isArray(body) || !body.every(isCardPinView)) {
      return { status: "unavailable" };
    }
    return { status: "ok", pins: body };
  } catch {
    return { status: "unavailable" };
  }
}

/** Исход закрепления карточки. Демон отвечает `200`: повтор идемпотентен. */
export type CardPinAddResult =
  | { status: "ok"; pin: CardPinView }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

export async function addCardPin(instrument: InstrumentRef): Promise<CardPinAddResult> {
  let response: Response;
  try {
    response = await fetch(CARD_PINS_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(instrument),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (!response.ok) {
    console.error("карточка не закреплена демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isCardPinView(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", pin: body };
  } catch {
    return { status: "unavailable" };
  }
}

/** Исход снятия закрепления: `204` и на отсутствующую запись. */
export type CardPinRemoveResult =
  | { status: "ok" }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

export async function removeCardPin(instrument: InstrumentRef): Promise<CardPinRemoveResult> {
  let response: Response;
  try {
    response = await fetch(CARD_PINS_BASE, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(instrument),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (response.status === 204) {
    return { status: "ok" };
  }
  console.error("закрепление карточки не снято демоном", response.status);
  return { status: "unavailable" };
}
